import React, { useState } from 'react';
import { validateLeadAgainstCompanyValues, checkOpenAIStatus } from '../../utils/lead_validation';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogContentText, 
  DialogActions, 
  Button, 
  CircularProgress,
  Typography,
  Chip,
  Box,
  Paper,
  Badge,
  Divider,
  Alert,
  AlertTitle
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import ThumbUpIcon from '@material-ui/icons/ThumbUp';
import ThumbDownIcon from '@material-ui/icons/ThumbDown';
import ErrorIcon from '@material-ui/icons/Error';
import InfoIcon from '@material-ui/icons/Info';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import DatabaseIcon from '@material-ui/icons/Storage';
import WarningIcon from '@material-ui/icons/Warning';

const styles = theme => ({
  root: {
    margin: 0,
    padding: theme.spacing(2),
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
  validIcon: {
    color: 'green',
    marginRight: theme.spacing(1),
  },
  invalidIcon: {
    color: 'red',
    marginRight: theme.spacing(1),
  },
  warningIcon: {
    color: 'orange',
    marginRight: theme.spacing(1),
  },
  message: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  chipContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    '& > *': {
      margin: theme.spacing(0.5),
    },
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  statusPaper: {
    padding: theme.spacing(1),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: '#f5f5f5',
  },
  apiStatusSuccess: {
    color: 'green',
  },
  apiStatusError: {
    color: 'red',
  },
  apiStatusWarning: {
    color: 'orange',
  },
  openAIStatusContainer: {
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.spacing(1),
  },
  openAIStatusText: {
    marginLeft: theme.spacing(1),
    fontSize: '0.875rem',
  },
  statusContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  alertBox: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  divider: {
    margin: `${theme.spacing(2)}px 0`,
  },
  databaseStatusContainer: {
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.spacing(1),
  },
  statusSummary: {
    backgroundColor: '#f8f8f8',
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    border: '1px solid #e0e0e0',
  }
});

/**
 * Component that validates lead content for professional standards
 * @param {Object} props - Component properties
 * @param {Object} props.lead - The lead data to validate
 * @param {string} props.companyID - The company ID
 * @param {function} props.onClose - Function to call when dialog is closed
 * @param {function} props.onSubmit - Function to call when lead is validated and submitted
 * @param {boolean} props.open - Whether the dialog is open
 */
const LeadValidator = ({ classes, lead, companyID, onClose, onSubmit, open }) => {
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [openAIStatus, setOpenAIStatus] = useState(null);
  
  const handleValidate = async () => {
    if (!lead || !companyID) {
      
      return;
    }

    setValidating(true);
    try {
      // Check OpenAI status first
      const apiStatus = await checkOpenAIStatus();
      setOpenAIStatus(apiStatus);
      
      
      // Continue with validation even if OpenAI is not working
      // The validation function will handle this case
      const result = await validateLeadAgainstCompanyValues(lead, companyID);
      
      
      // Still log detailed information to console
      if (result.matchingValues && result.matchingValues.length > 0) {
        
      }
      
      
      
      
      
      if (result.errorDetails) {
        
      }
      
      setValidationResult(result);
    } catch (error) {
      
      setValidationResult({
        valid: true, // Default to valid in case of error
        message: 'An error occurred during validation. Your lead will still be submitted.',
        apiStatus: 'component_error',
        errorDetails: error.message,
        openAIStatus: { working: false, error: error.message }
      });
    } finally {
      setValidating(false);
    }
  };

  const handleClose = () => {
    setValidationResult(null);
    setOpenAIStatus(null);
    onClose();
  };

  const handleSubmit = () => {
    onSubmit();
    handleClose();
  };

  // Start validation when dialog opens
  React.useEffect(() => {
    if (open && lead && companyID && !validationResult && !validating) {
      handleValidate();
    }
  }, [open, lead, companyID]);

  // Determine if we should allow submission even without validation
  const shouldAllowSubmission = () => {
    if (!validationResult) return false;
    
    // If valid, allow submission
    if (validationResult.valid) return true;
    
    // If no company values were found, allow submission
    if (validationResult.apiStatus === 'no_values') return true;
    
    // If OpenAI is not available, allow submission
    if (validationResult.apiStatus === 'openai_unavailable') return true;
    
    // If there was an API error, allow submission
    if (validationResult.apiStatus && 
       (validationResult.apiStatus === 'api_error' || 
        validationResult.apiStatus === 'error' ||
        validationResult.apiStatus === 'component_error')) {
      return true;
    }
    
    return false;
  };

  // Render OpenAI status
  const renderOpenAIStatus = () => {
    if (!openAIStatus && !validationResult?.openAIStatus) return null;
    
    const status = openAIStatus || validationResult.openAIStatus;
    
    return (
      <div className={classes.openAIStatusContainer}>
        {status.working ? (
          <CheckCircleIcon style={{ color: 'green', fontSize: '1rem' }} />
        ) : (
          <CancelIcon style={{ color: 'red', fontSize: '1rem' }} />
        )}
        <Typography variant="body2" className={classes.openAIStatusText}>
          OpenAI API: {status.working ? 'Available' : 'Not Available'}
          {status.error && ` - Error: ${status.error}`}
        </Typography>
      </div>
    );
  };

  // Render database status
  const renderDatabaseStatus = () => {
    if (!validationResult || !validationResult.apiStatus) return null;
    
    const isDataAvailable = validationResult.apiStatus !== 'no_values';
    
    return (
      <div className={classes.databaseStatusContainer}>
        {isDataAvailable ? (
          <CheckCircleIcon style={{ color: 'green', fontSize: '1rem' }} />
        ) : (
          <CancelIcon style={{ color: 'orange', fontSize: '1rem' }} />
        )}
        <Typography variant="body2" className={classes.openAIStatusText}>
          Company Values: {isDataAvailable ? 'Available' : 'Not Found in Database'}
        </Typography>
      </div>
    );
  };

  // Render status alert for fetch issues
  const renderStatusAlert = () => {
    if (!validationResult) return null;
    
    const hasDatabaseIssue = validationResult.apiStatus === 'no_values';
    const hasApiIssue = validationResult.apiStatus === 'openai_unavailable' || 
                        validationResult.apiStatus === 'api_error';
    
    if (!hasDatabaseIssue && !hasApiIssue) return null;
    
    return (
      <div className={classes.alertBox}>
        {hasDatabaseIssue && (
          <Paper elevation={0} style={{ backgroundColor: '#fff3e0', padding: '10px', marginBottom: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start' }}>
              <WarningIcon style={{ color: '#ff9800', marginRight: '10px', marginTop: '2px' }} />
              <div>
                <Typography variant="subtitle2" style={{ color: '#e65100' }}>
                  Company Values Not Found
                </Typography>
                <Typography variant="body2">
                  No company values were found in the database for your company. 
                  Your lead will be submitted without validation. 
                  Please contact your administrator to set up company values.
                </Typography>
              </div>
            </div>
          </Paper>
        )}
        
        {hasApiIssue && (
          <Paper elevation={0} style={{ backgroundColor: '#ffebee', padding: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start' }}>
              <ErrorIcon style={{ color: '#f44336', marginRight: '10px', marginTop: '2px' }} />
              <div>
                <Typography variant="subtitle2" style={{ color: '#c62828' }}>
                  OpenAI API Not Available
                </Typography>
                <Typography variant="body2">
                  The OpenAI API is currently not responding or unavailable. 
                  Your lead will be submitted without validation.
                </Typography>
              </div>
            </div>
          </Paper>
        )}
      </div>
    );
  };

  // Render API status information
  const renderApiStatus = () => {
    if (!validationResult || !validationResult.apiStatus) return null;
    
    const getStatusText = (status) => {
      switch (status) {
        case 'success':
          return 'OpenAI validation successful (JSON format)';
        case 'success_text_analysis':
          return 'OpenAI validation successful (text analysis)';
        case 'api_error':
          return 'Error with OpenAI API';
        case 'no_answer':
          return 'No answer received from OpenAI';
        case 'no_values':
          return 'No company values found for validation';
        case 'error':
          return 'Error during validation process';
        case 'component_error':
          return 'Error in validation component';
        case 'openai_unavailable':
          return 'OpenAI API is not available';
        default:
          return `Unknown status: ${status}`;
      }
    };
    
    const getStatusClass = (status) => {
      if (status.includes('success')) return classes.apiStatusSuccess;
      if (status.includes('error') || status === 'openai_unavailable') return classes.apiStatusError;
      return classes.apiStatusWarning;
    };
    
    return (
     
        <div className={classes.statusContainer}>
          {/* <Typography variant="body2">
            <InfoIcon fontSize="small" style={{ verticalAlign: 'middle', marginRight: '8px' }} />
            <span className={getStatusClass(validationResult.apiStatus)}>
              Validation Status: {getStatusText(validationResult.apiStatus)}
            </span>
          </Typography> */}
          
          {/* {renderOpenAIStatus()}
          {renderDatabaseStatus()} */}
          
          {validationResult.errorDetails && (
            <Typography variant="caption" color="error">
              Error details: {validationResult.errorDetails}
            </Typography>
          )}
        </div>
      
    );
  };

  // Render content issues if any
  const renderContentIssues = () => {
    if (!validationResult || !validationResult.contentIssue) {
      return null;
    }
    
    const issueLabels = {
      'obscene': 'Inappropriate Content',
      'profanity': 'Profanity Detected',
      'senseless': 'Content Quality Issue'
    };
    
    return (
      <Box>
        <Typography variant="subtitle2" color="error">Content Issue Detected:</Typography>
        <div className={classes.chipContainer}>
          <Chip 
            label={issueLabels[validationResult.contentIssue] || validationResult.contentIssue} 
            color="secondary" 
            variant="outlined" 
          />
        </div>
      </Box>
    );
  };

  // Render a status summary at the top of the dialog
  const renderStatusSummary = () => {
    if (!validationResult) return null;
    
    const hasDatabaseIssue = validationResult.apiStatus === 'no_values';
    const hasApiIssue = validationResult.apiStatus === 'openai_unavailable' || 
                        validationResult.apiStatus === 'api_error';
    
    if (!hasDatabaseIssue && !hasApiIssue) return null;
    
    return (
      <div className={classes.statusSummary}>
        <Typography variant="subtitle1" gutterBottom style={{ fontWeight: 500 }}>
          Content Validation Status
        </Typography>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <DatabaseIcon style={{ 
              marginRight: '8px', 
              color: hasDatabaseIssue ? 'orange' : 'green', 
              fontSize: '1.2rem' 
            }} />
            <Typography variant="body2">
              Content Filter: {hasDatabaseIssue ? 'Basic Check Only' : 'Full Validation'}
            </Typography>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {hasApiIssue ? (
              <CancelIcon style={{ marginRight: '8px', color: 'red', fontSize: '1.2rem' }} />
            ) : (
              <CheckCircleIcon style={{ marginRight: '8px', color: 'green', fontSize: '1.2rem' }} />
            )}
            <Typography variant="body2">
              AI Content Check: {hasApiIssue ? 'Not Available' : 'Available'}
            </Typography>
          </div>
          
          <Typography variant="body2" style={{ marginTop: '4px' }}>
            {hasDatabaseIssue && hasApiIssue 
              ? 'Content validation is unavailable. Your lead will be submitted without filtering.'
              : hasDatabaseIssue 
                ? 'Using basic content check. Your lead will be submitted.'
                : 'Content validation is unavailable. Your lead will be submitted without filtering.'
            }
          </Typography>
        </div>
      </div>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="lead-validation-dialog-title"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="lead-validation-dialog-title" className={classes.root}>
        Content Review
        <IconButton aria-label="close" className={classes.closeButton} onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {validating ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <CircularProgress />
            <Typography variant="body1" style={{ marginTop: '10px' }}>
              Checking content for professional standards...
            </Typography>
          </div>
        ) : validationResult ? (
          <div>
            {renderStatusSummary()}
            
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {validationResult.apiStatus === 'openai_unavailable' ? (
                <ErrorIcon style={{ color: 'red', marginRight: '10px' }} />
              ) : validationResult.apiStatus === 'no_values' ? (
                <InfoIcon style={{ color: 'orange', marginRight: '10px' }} />
              ) : validationResult.valid ? (
                <ThumbUpIcon className={classes.validIcon} />
              ) : (
                <ThumbDownIcon className={classes.invalidIcon} />
              )}
              <Typography variant="h6">
                {validationResult.apiStatus === 'openai_unavailable'
                  ? 'Content validation is not available'
                  : validationResult.apiStatus === 'no_values'
                    ? 'Content validation completed'
                    : validationResult.valid 
                      ? 'Your content meets professional standards!' 
                      : 'Your content needs review.'}
              </Typography>
            </div>
            
            {renderStatusAlert()}
            
            {!(validationResult.apiStatus === 'no_values' || validationResult.apiStatus === 'openai_unavailable') && (
              <DialogContentText className={classes.message}>
                {validationResult.message}
              </DialogContentText>
            )}
            
            {renderContentIssues()}
            
            <Divider className={classes.divider} />
            
            {renderApiStatus()}
          </div>
        ) : (
          <DialogContentText>
            Preparing to validate your lead...
          </DialogContentText>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          Cancel
        </Button>
        {validationResult && (
          <Button 
            onClick={handleSubmit} 
            color="primary" 
            variant="contained"
            disabled={!shouldAllowSubmission()}
          >
            {validationResult.valid 
              ? 'Submit Lead' 
              : validationResult.apiStatus === 'no_values' || validationResult.apiStatus === 'openai_unavailable'
                ? 'Submit Lead' 
                : 'Revise Content'
            }
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default withStyles(styles)(LeadValidator); 